import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "namespace-creation-note" }
const _hoisted_2 = ["href"]
const _hoisted_3 = { class: "sr-only" }

import { computed, watch } from 'vue';
import { useStore } from 'vuex';
import { RcSection } from '@components/RcSection';
import Checkbox from '@components/Form/Checkbox/Checkbox.vue';
import { RadioGroup } from '@components/Form/Radio';
import { RcIcon } from '@components/RcIcon';
import { RcContentGroup } from '@components/Layout';
import FleetPolicyAllowList from '@shell/components/fleet/FleetPolicyAllowList.vue';
import { useI18n } from '@shell/composables/useI18n';
import { getPolicyNamespaceCreationDocsUrl } from '@shell/utils/fleet-docs';
import type { FleetPolicy } from '@shell/types/fleet';


export default /*@__PURE__*/_defineComponent({
  __name: 'FleetPolicyServiceAccountsSection',
  props: /*@__PURE__*/_mergeModels({
    value: {},
    mode: {},
    serviceAccountOptions: { default: () => [] }
  }, {
    "restricted": { type: Boolean, ...{ default: false } },
    "restrictedModifiers": {},
  }),
  emits: ["update:restricted"],
  setup(__props: any) {

const props = __props;

const { t } = useI18n(useStore());

const restricted = _useModel<boolean>(__props, 'restricted');

const requireServiceAccount = computed({
  get: () => !!props.value.requireServiceAccount,
  set: (val: boolean) => {
    props.value.requireServiceAccount = val;
  }
});

const allowNamespaceCreation = computed({
  get: () => !!props.value.allowNamespaceCreation,
  set: (val: boolean) => {
    props.value.allowNamespaceCreation = val;
  }
});

const namespaceCreationDocsUrl = getPolicyNamespaceCreationDocsUrl();

const allowedServiceAccounts = computed(() => props.value.allowedServiceAccounts || []);

const restrictOptions = [
  { value: false, label: t('fleet.policy.serviceAccounts.restrict.all') },
  {
    value:       true,
    label:       t('fleet.policy.serviceAccounts.restrict.selected'),
    description: t('fleet.policy.serviceAccounts.restrict.description'),
  },
];

// Fleet reads a list left behind as a restriction, so going back to allowing all has to clear it.
// The names are kept for the rest of the session instead of being thrown away, so changing the
// choice back restores them.
let clearedServiceAccounts: string[] = [];

watch(restricted, (val) => {
  if (!val) {
    clearedServiceAccounts = [...allowedServiceAccounts.value];
    props.value.allowedServiceAccounts = [];
  } else if (!allowedServiceAccounts.value.length) {
    props.value.allowedServiceAccounts = [...clearedServiceAccounts];
  }
});

// Fleet unions this across the namespace, so a value left behind here would re-enable namespace
// creation for any other policy that does require a service account
watch(requireServiceAccount, (required) => {
  if (!required && props.value.allowNamespaceCreation) {
    props.value.allowNamespaceCreation = false;
  }
});

const updateAllowed = (val: string[]) => {
  props.value.allowedServiceAccounts = val;
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(RcSection), {
    title: _unref(t)('fleet.policy.serviceAccounts.title'),
    mode: "with-header",
    type: "primary",
    expandable: "",
    "data-testid": "fleet-policy-service-accounts"
  }, {
    default: _withCtx(() => [
      _createVNode(_unref(RcContentGroup), null, {
        default: _withCtx(() => [
          _createVNode(Checkbox, {
            value: requireServiceAccount.value,
            "onUpdate:value": _cache[0] || (_cache[0] = ($event: any) => ((requireServiceAccount).value = $event)),
            mode: props.mode,
            label: _unref(t)('fleet.policy.serviceAccounts.require.label'),
            description: _unref(t)('fleet.policy.serviceAccounts.require.description'),
            "data-testid": "fleet-policy-require-service-account"
          }, null, 8, ["value", "mode", "label", "description"]),
          _cache[5] || (_cache[5] = _createTextVNode()),
          (requireServiceAccount.value)
            ? (_openBlock(), _createBlock(_unref(RcSection), {
                key: 0,
                title: _unref(t)('fleet.policy.serviceAccounts.allowNamespaceCreation.title'),
                mode: "with-header",
                type: "secondary",
                expandable: "",
                "data-testid": "fleet-policy-namespace-creation"
              }, {
                default: _withCtx(() => [
                  _createElementVNode("div", _hoisted_1, [
                    _createElementVNode("p", null, _toDisplayString(_unref(t)('fleet.policy.serviceAccounts.allowNamespaceCreation.description')), 1),
                    _cache[3] || (_cache[3] = _createTextVNode()),
                    _createElementVNode("a", {
                      href: _unref(namespaceCreationDocsUrl),
                      target: "_blank",
                      rel: "noopener noreferrer nofollow",
                      "data-testid": "fleet-policy-namespace-creation-docs-link"
                    }, [
                      _createTextVNode(_toDisplayString(_unref(t)('fleet.policy.serviceAccounts.allowNamespaceCreation.link')) + " ", 1),
                      _createVNode(_unref(RcIcon), {
                        type: "external-link",
                        size: "small",
                        "aria-hidden": true
                      }),
                      _createElementVNode("span", _hoisted_3, _toDisplayString(_unref(t)('generic.opensInNewTab')), 1)
                    ], 8, _hoisted_2)
                  ]),
                  _cache[4] || (_cache[4] = _createTextVNode()),
                  _createVNode(Checkbox, {
                    value: allowNamespaceCreation.value,
                    "onUpdate:value": _cache[1] || (_cache[1] = ($event: any) => ((allowNamespaceCreation).value = $event)),
                    mode: props.mode,
                    label: _unref(t)('fleet.policy.serviceAccounts.allowNamespaceCreation.label'),
                    "data-testid": "fleet-policy-allow-namespace-creation"
                  }, null, 8, ["value", "mode", "label"])
                ]),
                _: 1
              }, 8, ["title"]))
            : _createCommentVNode("", true)
        ]),
        _: 1
      }),
      _cache[7] || (_cache[7] = _createTextVNode()),
      _createVNode(_unref(RcContentGroup), null, {
        default: _withCtx(() => [
          _createVNode(_unref(RadioGroup), {
            value: restricted.value,
            "onUpdate:value": _cache[2] || (_cache[2] = ($event: any) => ((restricted).value = $event)),
            name: "fleet-policy-restrict-service-accounts",
            options: restrictOptions,
            mode: props.mode,
            "aria-label": _unref(t)('fleet.policy.serviceAccounts.title'),
            "data-testid": "fleet-policy-restrict-service-accounts"
          }, null, 8, ["value", "mode", "aria-label"]),
          _cache[6] || (_cache[6] = _createTextVNode()),
          (restricted.value)
            ? (_openBlock(), _createBlock(FleetPolicyAllowList, {
                key: 0,
                value: allowedServiceAccounts.value,
                title: _unref(t)('fleet.policy.serviceAccounts.allowed.title'),
                label: _unref(t)('fleet.policy.serviceAccounts.allowed.label'),
                options: props.serviceAccountOptions,
                mode: props.mode,
                "data-testid": "fleet-policy-allowed-service-accounts",
                "onUpdate:value": updateAllowed
              }, null, 8, ["value", "title", "label", "options", "mode"]))
            : _createCommentVNode("", true)
        ]),
        _: 1
      })
    ]),
    _: 1
  }, 8, ["title"]))
}
}

})